/**
 * Quality Domination — design tokens (LIGHT / parchment mode).
 *
 * Token names retain their chalk-on-slate semantics for backwards
 * compatibility, but their VALUES are flipped so the whole UI now reads as
 * ink-on-parchment — a vintage Risk-board aesthetic that keeps every
 * existing `surfaces.X` reference working without code edits:
 *
 *   - `slate`  was the dark blackboard background; now the warm parchment
 *               field that the entire app sits on.
 *   - `chalk`  was the light cream pigment for text and strokes; now the
 *               dark ink used for text and strokes.
 *   - `paper`  was a cream accent for cards; still cream, slightly lighter
 *               than the parchment field so cards stand off the surface.
 *   - `graphite` / `ink` stay dark; they were already used as dark strokes.
 *   - `waxRed` is unchanged — works equally well on slate or parchment.
 */

export const surfaces = {
  slate: '#0284c7', // vibrant ocean blue
  slateShadow: '#0369a1',
  paper: '#ffffff', // clean white
  paperShadow: '#e2e8f0', // soft grey shadow
  chalk: '#ffffff', // crisp white text
  graphite: '#1e293b', // dark bold text
  ink: '#0f172a',
  waxRed: '#ef4444', // vibrant red
} as const;

export type SurfaceColor = keyof typeof surfaces;

export const playerColors = {
  prussian: { hex: '#3b82f6', name: 'Royal Blue' },
  vermillion: { hex: '#ef4444', name: 'Bright Red' },
  mustard: { hex: '#eab308', name: 'Sun Yellow' },
  verdigris: { hex: '#10b981', name: 'Emerald Green' },
  tyrian: { hex: '#8b5cf6', name: 'Vibrant Purple' },
  bone: { hex: '#f97316', name: 'Cheerful Orange' },
} as const;

export type PlayerColorKey = keyof typeof playerColors;

export const fonts = {
  display: 'Fraunces',
  body: 'Newsreader',
  mono: 'Departure Mono',
} as const;

export const fontStacks = {
  display: `system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'`,
  body: `system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'`,
  mono: `'Departure Mono', 'JetBrains Mono', ui-monospace, monospace`,
} as const;

export const textures = {
  chalkStroke: {
    baseFrequency: 0.7,
    numOctaves: 1,
    displacementScale: 1.8,
    strokeOpacity: 0.78,
    strokeWidth: 1.4,
  },
  paperGrain: {
    baseFrequency: 2.4,
    numOctaves: 3,
    opacity: 0.08,
    blendMode: 'multiply' as const,
  },
  slateNoise: {
    baseFrequency: 0.9,
    numOctaves: 2,
    opacity: 0.06,
    blendMode: 'overlay' as const,
  },
} as const;
